import React, { useEffect, useState } from 'react'
import { Dialog } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useTenantMaintenance } from '@/hooks/useTenantMaintenance'

/**
 * Full-screen blocking overlay shown while the tenant is in maintenance mode
 * (toggled from FitUP Commander via Firestore). Renders above every modal,
 * traps focus, and has no close affordance — the only way out is Commander
 * turning maintenance off, which dismisses it in real time.
 *
 * Visuals ported from the approved FitUP maintenance page design (light-only,
 * FitUP design tokens hardcoded so it looks identical in dark mode too). The
 * countdown and window line only render when Commander has written a
 * `maintenance.endAt` schedule to Firestore.
 */

// FitUP design tokens from the approved maintenance page mock.
const Root = styled('div')`
  --primary: #de2826;
  --primary-deep: #b90810;
  --primary-container: #f8d3d3;
  --secondary-container: #fce9e9;
  --surface: #ffffff;
  --surface-low: #fafafa;
  --surface-container: #f1f1f1;
  --on-surface: #000000;
  --on-surface-variant: #8e8e8e;
  --outline: #bcbcbc;
  --outline-variant: #e0e0e0;

  height: 100%;
  font-family:
    'Anuphan',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    system-ui,
    sans-serif;
  color: var(--on-surface);
  background: var(--surface-low);
  -webkit-font-smoothing: antialiased;
  display: grid;
  place-items: center;
  overflow: auto;
  padding: clamp(16px, 4vw, 48px);
  /* faint brand glow — not a full wash */
  background-image: radial-gradient(
    120% 90% at 50% -10%,
    rgba(222, 40, 38, 0.08) 0%,
    rgba(222, 40, 38, 0) 55%
  );

  .card {
    position: relative;
    width: min(680px, 100%);
    background: var(--surface);
    border-radius: 28px;
    box-shadow:
      0 4px 4px rgba(0, 0, 0, 0.3),
      0 8px 12px 6px rgba(0, 0, 0, 0.15);
    padding: clamp(28px, 5vw, 56px);
    text-align: center;
    overflow: hidden;
  }
  .card::before {
    /* top brand hairline */
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary) 0%, var(--primary-deep) 60%, #7e5260 100%);
  }

  .brand {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: clamp(20px, 4vw, 32px);
  }
  .brand img {
    height: 44px;
    display: block;
  }

  .art {
    position: relative;
    width: 148px;
    height: 148px;
    margin: 0 auto clamp(20px, 4vw, 28px);
  }
  .art .halo {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--secondary-container);
  }
  .art .halo::after {
    content: '';
    position: absolute;
    inset: 14px;
    border-radius: 50%;
    border: 2px dashed var(--primary-container);
    animation: od-spin 22s linear infinite;
  }
  .art svg {
    position: absolute;
  }
  .gear-a {
    top: 30px;
    left: 30px;
    width: 62px;
    height: 62px;
    color: var(--primary);
  }
  .gear-b {
    top: 66px;
    left: 74px;
    width: 44px;
    height: 44px;
    color: var(--outline);
  }
  .gear path {
    transform-box: fill-box;
    transform-origin: center;
  }
  .gear-a path {
    animation: od-spin 9s linear infinite;
  }
  .gear-b path {
    animation: od-spin-r 9s linear infinite;
  }
  @keyframes od-spin {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes od-spin-r {
    to {
      transform: rotate(-360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .art .halo::after,
    .gear path {
      animation: none;
    }
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--primary-deep);
    background: var(--secondary-container);
    padding: 5px 12px;
    border-radius: 9999px;
    margin-bottom: 16px;
  }
  .status-pill .live {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--primary);
    box-shadow: 0 0 0 0 rgba(222, 40, 38, 0.5);
    animation: od-pulse 1.8s ease-out infinite;
  }
  @keyframes od-pulse {
    70% {
      box-shadow: 0 0 0 8px rgba(222, 40, 38, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(222, 40, 38, 0);
    }
  }
  h1 {
    margin: 0 0 8px;
    font-size: clamp(26px, 4.5vw, 36px);
    line-height: 1.2;
    font-weight: 700;
  }
  .lead {
    margin: 0 auto clamp(24px, 4vw, 32px);
    max-width: 46ch;
    font-size: clamp(15px, 2.2vw, 16px);
    line-height: 1.6;
    color: var(--on-surface-variant);
    text-wrap: pretty;
  }

  .cd {
    display: flex;
    justify-content: center;
    gap: clamp(8px, 2vw, 14px);
    margin-bottom: 24px;
  }
  .cd-cell {
    flex: 0 1 116px;
    background: var(--surface-low);
    border: 1px solid var(--outline-variant);
    border-radius: 12px;
    padding: clamp(12px, 2.4vw, 18px) 8px 8px;
  }
  .cd-num {
    font-size: clamp(30px, 7vw, 46px);
    line-height: 1;
    font-weight: 700;
    color: var(--on-surface);
    font-variant-numeric: tabular-nums;
  }
  .cd-lab {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--on-surface-variant);
  }
  .cd.done .cd-num {
    color: var(--primary);
  }

  .track {
    height: 6px;
    border-radius: 9999px;
    background: var(--surface-container);
    overflow: hidden;
    margin-bottom: 10px;
    position: relative;
  }
  .track .bar {
    position: absolute;
    inset: 0 auto 0 0;
    width: 38%;
    border-radius: 9999px;
    background: linear-gradient(90deg, var(--primary), var(--primary-deep));
    animation: od-sweep 2.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }
  @keyframes od-sweep {
    0% {
      left: -40%;
    }
    100% {
      left: 100%;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .track .bar {
      animation: none;
      left: 0;
      width: 62%;
    }
  }
  .window {
    font-size: 13px;
    color: var(--on-surface-variant);
    margin: 0 0 clamp(24px, 4vw, 32px);
  }
  .window strong {
    color: var(--on-surface);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  .foot {
    margin-top: clamp(24px, 4vw, 32px);
    padding-top: 16px;
    border-top: 1px solid var(--outline-variant);
    font-size: 12px;
    color: var(--on-surface-variant);
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    justify-content: center;
    align-items: center;
  }
  .foot .ref {
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 460px) {
    .cd {
      flex-wrap: wrap;
    }
    .cd-cell {
      flex: 1 1 42%;
    }
    .brand img {
      height: 38px;
    }
  }
`

const GEAR_PATH =
  'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z'

const pad = (n: number): string => String(n).padStart(2, '0')

const fmtTime = (ms: number): string => {
  const d = new Date(ms)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Ticks every second while active so the countdown re-renders live. */
const useNowTicker = (active: boolean): number => {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!active) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [active])

  return now
}

export const MaintenanceOverlay: React.FC = () => {
  const { t } = useTranslation()
  const maintenance = useTenantMaintenance()

  const endMs = maintenance.endAt ? new Date(maintenance.endAt).getTime() : null
  const startMs = maintenance.updatedAt ? new Date(maintenance.updatedAt).getTime() : null
  const hasCountdown = maintenance.enabled && endMs !== null && !Number.isNaN(endMs)

  const now = useNowTicker(hasCountdown)
  const isDone = hasCountdown && endMs - now <= 0
  const totalSeconds = hasCountdown ? Math.max(0, Math.floor((endMs - now) / 1000)) : 0

  // Reference code — stable per maintenance window, not random noise.
  const refSourceMs = endMs ?? startMs
  const refCode =
    refSourceMs !== null && !Number.isNaN(refSourceMs)
      ? `MNT-${Math.floor(refSourceMs / 60000)
          .toString(36)
          .toUpperCase()
          .slice(-6)}`
      : '—'

  return (
    <Dialog
      fullScreen
      open={maintenance.enabled}
      disableEscapeKeyDown
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <Root>
        <main className="card" role="main">
          <div className="brand">
            <img src="/images/fitup-logo.svg" alt="FitUP Manager" />
          </div>

          <div className="art" aria-hidden="true">
            <span className="halo" />
            <svg className="gear gear-a" viewBox="0 0 24 24" fill="currentColor">
              <path d={GEAR_PATH} />
            </svg>
            <svg className="gear gear-b" viewBox="0 0 24 24" fill="currentColor">
              <path d={GEAR_PATH} />
            </svg>
          </div>

          <span className="status-pill">
            <span className="live" />
            {isDone ? t('maintenance-mode-ready-status') : t('maintenance-mode-status')}
          </span>
          <h1>{isDone ? t('maintenance-mode-ready-title') : t('maintenance-mode-title')}</h1>
          <p className="lead">{t('maintenance-mode-message')}</p>

          {hasCountdown && (
            <div className={isDone ? 'cd done' : 'cd'} role="timer" aria-live="polite">
              <div className="cd-cell">
                <div className="cd-num">{pad(Math.floor(totalSeconds / 86400))}</div>
                <span className="cd-lab">{t('maintenance-mode-days')}</span>
              </div>
              <div className="cd-cell">
                <div className="cd-num">{pad(Math.floor((totalSeconds % 86400) / 3600))}</div>
                <span className="cd-lab">{t('maintenance-mode-hours')}</span>
              </div>
              <div className="cd-cell">
                <div className="cd-num">{pad(Math.floor((totalSeconds % 3600) / 60))}</div>
                <span className="cd-lab">{t('maintenance-mode-minutes')}</span>
              </div>
              <div className="cd-cell">
                <div className="cd-num">{pad(totalSeconds % 60)}</div>
                <span className="cd-lab">{t('maintenance-mode-seconds')}</span>
              </div>
            </div>
          )}

          <div className="track" aria-hidden="true">
            <span className="bar" />
          </div>
          {hasCountdown && startMs !== null && !Number.isNaN(startMs) && (
            <p className="window">
              {t('maintenance-mode-window')}{' '}
              <strong>
                {fmtTime(startMs)} – {fmtTime(endMs)}
              </strong>{' '}
              · {t('maintenance-mode-timezone')}
            </p>
          )}

          <div className="foot">
            <span>© {new Date().getFullYear()} FitUP</span>
            <span aria-hidden="true">·</span>
            <span className="ref">Ref: {refCode}</span>
          </div>
        </main>
      </Root>
    </Dialog>
  )
}
